import { IComplianceSampleQuote } from 'src/app/interfaces/icompliance-sample-quote';
import { IComplianceSampleQuoteParts } from 'src/app/interfaces/icompliance-sample-quote-parts';

/** Longest quote shown, in characters; longer turns are trimmed around the flagged words. */
export const SAMPLE_QUOTE_MAX_LENGTH = 300;

/**
 * Splits the learner's turn around the flagged words (spanStart inclusive, spanEnd exclusive).
 * A turn longer than maxLength keeps the flagged words and trims the text around them at word
 * boundaries, with an ellipsis where text was cut. Invalid offsets show the turn without bold.
 */
export function splitSampleQuote(quote: IComplianceSampleQuote, maxLength: number = SAMPLE_QUOTE_MAX_LENGTH): IComplianceSampleQuoteParts {
  const text = quote.turnText ?? '';
  const { spanStart, spanEnd } = quote;
  const hasSpan = Number.isInteger(spanStart) && Number.isInteger(spanEnd) && spanStart >= 0 && spanStart < spanEnd && spanEnd <= text.length;
  const start = hasSpan ? spanStart : 0;
  const end = hasSpan ? spanEnd : 0;

  let before = text.slice(0, start);
  const flagged = text.slice(start, end);
  let after = text.slice(end);

  if (text.length > maxLength) {
    const room = Math.max(maxLength - flagged.length, 0);
    const beforeRoom = Math.min(before.length, hasSpan ? Math.floor(room / 2) : 0);
    const afterRoom = Math.min(after.length, room - beforeRoom);
    const extraBefore = Math.min(before.length - beforeRoom, room - beforeRoom - afterRoom);
    before = trimStart(before, beforeRoom + extraBefore);
    after = trimEnd(after, afterRoom);
  }

  return { before, flagged, after };
}

/** Keeps the last `keep` characters, dropping a word cut in half at the start. */
function trimStart(text: string, keep: number): string {
  if (keep >= text.length) return text;
  if (keep <= 0) return '…';
  const cut = text.length - keep;
  const kept = text.slice(cut);
  if (/\s/.test(text[cut - 1])) return '…' + kept;
  const wordStart = kept.search(/\s/);
  return '…' + (wordStart >= 0 ? kept.slice(wordStart + 1) : kept);
}

/** Keeps the first `keep` characters, dropping a word cut in half at the end. */
function trimEnd(text: string, keep: number): string {
  if (keep >= text.length) return text;
  if (keep <= 0) return '…';
  const kept = text.slice(0, keep);
  if (/\s/.test(text[keep])) return kept + '…';
  const wordEnd = kept.search(/\s\S*$/);
  return (wordEnd >= 0 ? kept.slice(0, wordEnd) : kept) + '…';
}
